import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef } from "react";
import { useSignal } from "@preact/signals";
import classNames from "classnames";
import TextInput from "../Form/TextInput";
import Icon from "../Icon";
import useOnClickOutside from "../../lib/hooks/use-on-click-outside";
import Button from "../Button";
import style from "./style.module.scss";
export function getOptionLabel(value, options) {
    for (const section of options) {
        for (const option of section.options) {
            if (option.value === value) {
                return option.label;
            }
        }
    }
    return value;
}
export function validateOptionValue(value, options, fallback) {
    if (typeof value !== "undefined") {
        for (const section of options) {
            for (const option of section.options) {
                if (option.value === value) {
                    return value;
                }
            }
        }
    }
    return fallback;
}
export default function DropDownMenu(props) {
    const { selected, buttonLabel, buttonClassName, buttonPrefix, menuListClassName, customIcon, searchable, searchPlaceHolder = "Search", specifyValue, sections, alignRight, onChange, placeholder, displayType, } = props;
    const searchTerm = useSignal("");
    const menuVisible = useSignal(false);
    const menuRef = useRef(null);
    useOnClickOutside(menuRef, () => (menuVisible.value = false));
    let selectedLabel = placeholder ? placeholder : "Please Select";
    let selectedDescription = "";
    if (specifyValue && selected) {
        selectedLabel = selected;
    }
    if (displayType && displayType === "icon") {
        selectedLabel = "";
    }
    function menuItems() {
        const menuItems = [];
        sections.forEach((section, sectionIndex) => {
            const sectionOptions = [];
            let selectedFound = false;
            section.options
                .filter((menuOption) => {
                return !searchTerm.value || menuOption.label.toLowerCase().includes(searchTerm.value.toLowerCase());
            })
                .forEach((menuOption) => {
                const descriptionContent = [];
                if (menuOption.value === selected) {
                    selectedFound = true;
                }
                if (menuOption.description) {
                    descriptionContent.push(_jsx("div", { className: style["drop-down-menu__section__option__description"], children: menuOption.description }));
                }
                sectionOptions.push(_jsx("li", { children: _jsxs(Button, { variation: "none", role: "menuitem", tabIndex: 0, className: classNames(style["drop-down-menu__section__option"], {
                            [style["drop-down-menu__section__option--selected"]]: menuOption.value === selected,
                            [style["drop-down-menu__section__option--description"]]: descriptionContent.length === 1,
                        }), onClick: (e) => {
                            e.preventDefault();
                            onOptionClicked(menuOption);
                        }, children: [menuOption.label, descriptionContent] }, menuOption.label) }));
            });
            //Add the current selected option as a special option if it's not in the list and specifyValue is true. Because the selected value is a previously defined specifyValue.
            if (!selectedFound && specifyValue && selected) {
                const prevSpecifyOption = {
                    label: selected,
                    value: selected,
                };
                sectionOptions.push(_jsx("li", { children: _jsx(Button, { variation: "none", role: "menuitem", className: classNames(style["drop-down-menu__section__option"], style["drop-down-menu__section__option--selected"]), onClick: (e) => {
                            e.preventDefault();
                            onOptionClicked(prevSpecifyOption);
                        }, children: selected }, "specify") }));
            }
            //Add user's current search term as an option at the end.
            if (searchTerm.value && specifyValue) {
                const term = "Specify: " + searchTerm.value;
                const specifyOption = {
                    label: searchTerm.value,
                    value: searchTerm.value,
                };
                sectionOptions.push(_jsx("li", { role: "menuitem", className: style["drop-down-menu__section__option"], onClick: (e) => {
                        e.preventDefault();
                        onOptionClicked(specifyOption);
                    }, children: term }, "specify2"));
            }
            if (sectionOptions.length) {
                //Add a divider if it's not the first section.
                if (sectionIndex !== 0) {
                    menuItems.push(_jsx("div", { className: style["drop-down-menu__section__divider"] }));
                }
                //Add the section title if it has one.
                if (section.title) {
                    menuItems.push(_jsx("div", { className: style["drop-down-menu__section__title"], children: section.title }));
                }
                menuItems.push(_jsx("ul", { className: style["drop-down-menu__section__options"], children: sectionOptions }));
            }
        });
        if (menuItems.length === 0) {
            if (searchTerm.value) {
                // Nothing matched the search term so display no result message.
                menuItems.push(_jsx("div", { className: style["drop-down-menu__section__options"], children: _jsx("span", { className: style["drop-down-menu__section__no-results"], children: "We couldn't find anything that matched the search term." }) }, "search-no-results"));
            }
            else if (!specifyValue) {
                // There are no options and the user can't specify a custom value
                menuItems.push(_jsx("div", { className: style["drop-down-menu__section__options"], children: _jsx("span", { className: style["drop-down-menu__section__no-results"], children: "No values available." }) }, "no-values"));
            }
        }
        return menuItems;
    }
    //Toggle the menu open if closed.
    function toggleMenu() {
        menuVisible.value = !menuVisible.value;
        if (menuVisible.value) {
            searchTerm.value = "";
        }
    }
    //Only render the search input if searching is enabled.
    function searchBox() {
        if (searchable) {
            return (_jsx("div", { className: style["drop-down-menu__search"], children: _jsx(TextInput, { placeholder: searchPlaceHolder, onInput: (event) => (searchTerm.value = event.currentTarget.value), clearable: true, spellCheck: false, value: searchTerm.value }) }));
        }
        else {
            return;
        }
    }
    //User has clicked on a menu option.
    function onOptionClicked(newOption) {
        onChange(newOption);
        menuVisible.value = false;
    }
    sections.forEach((section) => {
        section.options.forEach((option) => {
            if (option.value === selected) {
                selectedLabel = option.label;
                if (option.description) {
                    selectedDescription = option.description;
                }
                return;
            }
        });
    });
    if (buttonLabel) {
        selectedLabel = buttonLabel;
    }
    const icon = [];
    if (customIcon && displayType === "title-icon") {
        icon.push(customIcon);
    }
    else if (customIcon && displayType === "icon") {
        icon.push(customIcon);
    }
    else if (customIcon && displayType === "title-icon-carrot") {
        icon.push(_jsx("span", { className: style["drop-down-menu__selection__custom-icon"], children: customIcon }));
        icon.push(_jsx(Icon, { name: "caret--down" }));
    }
    else {
        icon.push(_jsx(Icon, { name: "caret--down" }));
    }
    const buttonDescription = [];
    if (selectedDescription !== "") {
        buttonDescription.push(_jsx("div", { className: style["drop-down-menu__selection__description"], children: selectedDescription }));
    }
    const prefix = [];
    if (buttonPrefix !== "") {
        prefix.push(_jsx("span", { className: style["drop-down-menu__selection__prefix"], children: buttonPrefix }));
    }
    return (_jsxs("div", { className: style["drop-down-menu"], ref: menuRef, children: [_jsxs(Button, { className: classNames(buttonClassName ? buttonClassName : style["drop-down-menu__selection"], displayType === "text-input" ? style["drop-down-menu__selection--text-input"] : null, displayType === "title-carrot-pill" ? style["drop-down-menu__selection--title-carrot-pill"] : null), variation: "none", "aria-haspopup": "true", onClick: toggleMenu, children: [_jsxs("div", { children: [_jsxs("div", { children: [prefix, selectedLabel] }), buttonDescription] }), icon] }), menuVisible.value ? (_jsxs("div", { className: classNames(style["drop-down-menu__menu"], menuListClassName, alignRight ? style["drop-down-menu__menu--right"] : null), children: [searchBox(), _jsx("div", { className: style["drop-down-menu__menu__content-container"], children: menuItems() })] })) : null] }));
}
